import React, { useState, useRef } from 'react';

// Explicações dos parâmetros para diferentes distribuições
export const PARAMETER_EXPLANATIONS = {
    norm: [
        "Média da distribuição (μ)",
        "Desvio padrão (σ)"
    ],

    expon: [
        "Deslocamento inicial da distribuição",
        "Escala (valor médio entre eventos)"
    ],

    lognorm: [
        "Desvio padrão do logaritmo (σ)",
        "Deslocamento no eixo X",
        "Escala da distribuição (e^μ)"
    ],

    gamma: [
        "Parâmetro de forma (α)",
        "Deslocamento no eixo X",
        "Parâmetro de escala (θ)"
    ],

    beta: [
        "Parâmetro de forma α",
        "Parâmetro de forma β",
        "Limite inferior do intervalo",
        "Amplitude do intervalo"
    ],

    weibull_min: [
        "Parâmetro de forma (k)",
        "Deslocamento no eixo X",
        "Parâmetro de escala (λ)"
    ],

    weibull_max: [
        "Parâmetro de forma (k)",
        "Deslocamento no eixo X",
        "Parâmetro de escala (λ)"
    ],

    pareto: [
        "Parâmetro de forma da cauda (α)",
        "Deslocamento no eixo X",
        "Valor mínimo da distribuição (xₘ)"
    ],

    gumbel_r: [
        "Parâmetro de localização (μ)",
        "Parâmetro de escala (β)"
    ],

    laplace: [
        "Valor central da distribuição",
        "Parâmetro de dispersão (b)"
    ],

    cauchy: [
        "Valor central da distribuição",
        "Largura da distribuição (γ)"
    ],

    t: [
        "Graus de liberdade (ν)",
        "Localização (μ)",
        "Escala (σ)"
    ]
};

// Função auxiliar para formatar números
const formatNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '-';
    if (typeof value !== 'number') return value;

    // Usar notação científica para valores muito pequenos (mas não zero)
    if (Math.abs(value) < 0.01 && value !== 0) {
        return value.toExponential(2);
    }

    return value.toFixed(2);
};

// Componente de Tooltip customizado para parâmetros de distribuição
export const ParameterTooltip = ({ distribution, parameters, children }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipOffset, setTooltipOffset] = useState(null);
    const containerRef = useRef(null);

    const getParameterExplanation = () => {
        const distLower = distribution.toLowerCase();
        const explanations = PARAMETER_EXPLANATIONS[distLower];

        if (!explanations) {
            return `Distribuição: ${distribution}\nParâmetros: ${parameters.map(p => formatNumber(p)).join(', ')}`;
        }

        let tooltip = `Distribuição: ${distribution}\n\n`;
        explanations.forEach((explanation, index) => {
            if (index < parameters.length) {
                tooltip += `${explanation}: ${formatNumber(parameters[index])}\n`;
            }
        });

        return tooltip.trim();
    };

    const handleMouseEnter = () => {
        setShowTooltip(true);

        // Calcular posição absoluta do tooltip relativa ao viewport
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const tooltipWidth = 250;
            const tooltipHeight = 120;
            const viewportWidth = window.innerWidth;

            // Posição inicial do tooltip (centralizado)
            let tooltipLeft = rect.left + rect.width / 2 - tooltipWidth / 2;
            let tooltipTop = rect.top - tooltipHeight - 10; // 10px acima

            // Ajustar horizontalmente se sair da tela
            if (tooltipLeft < 10) {
                tooltipLeft = 10;
            } else if (tooltipLeft + tooltipWidth > viewportWidth - 10) {
                tooltipLeft = viewportWidth - tooltipWidth - 10;
            }

            // Se não há espaço acima, coloca abaixo
            if (tooltipTop < 10) {
                tooltipTop = rect.bottom + 10;
            }

            setTooltipOffset({ left: tooltipLeft, top: tooltipTop, elementRect: rect });
        }
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
        setTooltipOffset(null);
    };

    const tooltipStyle = tooltipOffset ? {
        position: 'fixed',
        left: tooltipOffset.left,
        top: tooltipOffset.top,
        backgroundColor: '#1f2937',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '6px',
        fontSize: '12px',
        whiteSpace: 'pre-line',
        zIndex: 10000,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        minWidth: '200px',
        maxWidth: '250px',
        width: '250px',
        textAlign: 'left',
        lineHeight: '1.4',
        pointerEvents: 'none'
    } : {};

    const arrowStyle = tooltipOffset ? {
        position: 'fixed',
        left: tooltipOffset.elementRect.left + tooltipOffset.elementRect.width / 2 - 6,
        top: tooltipOffset.top < tooltipOffset.elementRect.top
            ? tooltipOffset.top + 120  // Seta abaixo do tooltip (tooltip acima do elemento)
            : tooltipOffset.top - 6,   // Seta acima do tooltip (tooltip abaixo do elemento)
        width: 0,
        height: 0,
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderTop: tooltipOffset.top < tooltipOffset.elementRect.top
            ? '6px solid #1f2937'     // Seta apontando para baixo
            : 'none',
        borderBottom: tooltipOffset.top >= tooltipOffset.elementRect.top
            ? '6px solid #1f2937'    // Seta apontando para cima
            : 'none',
        zIndex: 10000,
        pointerEvents: 'none'
    } : {};

    return (
        <div
            ref={containerRef}
            style={{ position: 'relative', display: 'inline-block', cursor: 'help' }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {children}
            {showTooltip && tooltipOffset && (
                <div style={tooltipStyle}>
                    {getParameterExplanation()}
                    <div style={arrowStyle} />
                </div>
            )}
        </div>
    );
};